import classNames from 'classnames';
import _ from 'lodash';
import type Quill from 'quill/core';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import useAppSelector from '../app/redux/useAppSelector';
import type { Boundary } from './types';
import { useUIElementResizeListener } from './UIElementResizeContext';
import useEditorWidthChange from './useEditorWidthChange';
import type Mark from './utils/Mark';
import styles from './CursorSelection.module.scss';
import getNestedTargetForRemoteSelection from './utils/getNestedTargetForRemoteSelection';

interface CursorSelectionProps {
  mark: Mark;
  quill: Quill;
  className?: string;
  user?: { index?: number };
}

const CursorSelection = ({
  mark,
  quill,
  className,
  user = {},
}: CursorSelectionProps) => {
  const version = useAppSelector((state) => state.editor.version);
  const [position, setPosition] = useState<{
    boundaries: Boundary[];
    quill: Quill;
  } | null>(null);

  const updatePosition = useCallback(() => {
    getNestedTargetForRemoteSelection(quill, mark).then((target) => {
      if (target) {
        setPosition({
          // @ts-expect-error
          boundaries: target.quill.getClientRects(
            target.range.index,
            target.range.length,
          ),
          quill: target.quill,
        });
      } else {
        setPosition(null);
      }
    });
  }, [quill, mark]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition, version]);

  const addUIElementResizeListener = useUIElementResizeListener();
  useEffect(() => {
    return addUIElementResizeListener(updatePosition);
  }, [updatePosition, addUIElementResizeListener]);

  useEditorWidthChange(position?.quill, updatePosition);

  if (!position || position.boundaries.length <= 0) return null;

  return createPortal(
    <div className={styles.container}>
      {_.map(position.boundaries, (bounds, i) => (
        <span
          key={i}
          className={classNames(
            styles.selection,
            user.index == null ? null : styles[`color${user.index + 1}`],
            className,
          )}
          style={{
            left: bounds.left,
            height: bounds.height,
            top: bounds.top,
            width: bounds.width,
          }}
        />
      ))}
    </div>,
    position.quill.container,
  );
};

export default CursorSelection;
